import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { auth, db } from "../Security/firebase-config";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [state, setState] = useState({ userData: null, loading: true, error: null });
  const [attempt, setAttempt] = useState(0);
  const refreshAuth = useCallback(() => {
    setState({ userData: null, loading: true, error: null });
    setAttempt((value) => value + 1);
  }, []);

  useEffect(() => {
    let alive = true;
    let generation = 0;
    let stopProfile;
    const stopAuth = onAuthStateChanged(auth, (user) => {
      const request = ++generation;
      stopProfile?.();
      setState({ userData: null, loading: Boolean(user), error: null });
      if (!user) return;

      stopProfile = onSnapshot(doc(db, "users", user.uid), { includeMetadataChanges: true }, (snapshot) => {
        if (!alive || request !== generation) return;
        // Wait for a saved team selection before asking the callable for access.
        if (snapshot.metadata.hasPendingWrites) return;
        // Signup owns profile creation. A missing profile is valid during onboarding.
        const profile = snapshot.exists() ? snapshot.data() : {
          email: user.email ?? null,
          username: user.displayName ?? null,
        };
        setState({ userData: { ...profile, uid: user.uid }, loading: false, error: null });
      }, (error) => {
        if (alive && request === generation) {
          setState({ userData: null, loading: false, error });
        }
      });
    }, (error) => {
      generation += 1;
      stopProfile?.();
      if (alive) setState({ userData: null, loading: false, error });
    });
    return () => {
      alive = false;
      generation += 1;
      stopProfile?.();
      stopAuth();
    };
  }, [attempt]);

  return <AuthContext.Provider value={{ ...state, refreshAuth }}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
